// Row 28.1 — in-app help drawer, backed by repo-owned markdown content (lib/docs/registry.ts).
// Composes the existing Modal primitive (same pattern HelpMenu already uses for Keyboard
// shortcuts) rather than inventing a new drawer primitive.
import { useState } from "react";
import type { ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { Modal } from "@perlab/ui";
import { DOC_PAGES } from "../../lib/docs/registry";
import { renderMarkdown } from "../../lib/docs/markdown";

export interface HelpDrawerProps {
  open: boolean;
  onClose: () => void;
}

export function HelpDrawer({ open, onClose }: HelpDrawerProps): ReactElement {
  const { t } = useTranslation();
  const [activeId, setActiveId] = useState(DOC_PAGES[0]?.id ?? "");
  const active = DOC_PAGES.find((p) => p.id === activeId) ?? DOC_PAGES[0];

  return (
    <Modal open={open} onClose={onClose} title={t("docs.title")}>
      <div className="flex gap-4">
        <nav className="flex w-40 shrink-0 flex-col gap-1" aria-label={t("docs.title")}>
          {DOC_PAGES.map((page) => (
            <button
              key={page.id}
              type="button"
              onClick={() => setActiveId(page.id)}
              aria-current={page.id === activeId ? "true" : undefined}
              className="rounded-sm px-2 py-1.5 text-left text-sm text-[var(--color-text-secondary)] aria-[current=true]:bg-[var(--color-surface-muted)] aria-[current=true]:text-[var(--color-text-primary)] hover:bg-[var(--color-surface-muted)]"
            >
              {t(page.titleKey)}
            </button>
          ))}
        </nav>
        <div
          className="min-w-0 flex-1 overflow-y-auto text-sm text-[var(--color-text-primary)] [&_h1]:mb-2 [&_h1]:text-lg [&_h1]:font-semibold [&_h2]:mb-1.5 [&_h2]:mt-3 [&_h2]:text-sm [&_h2]:font-semibold [&_p]:mb-2 [&_table]:w-full [&_td]:border-t [&_td]:border-[var(--color-border-subtle)] [&_td]:py-1 [&_th]:text-left"
          // eslint-disable-next-line react/no-danger -- sanitized via DOMPurify in renderMarkdown
          dangerouslySetInnerHTML={{ __html: active ? renderMarkdown(active.content) : "" }}
        />
      </div>
    </Modal>
  );
}
